/* sugar:assets/video-controls.js@2.2.0 */
/* Video controls engine.
 *
 * A controls overlay (.video-controls, rendered by the Custom/Simple Video
 * Controls blocks) binds to the video(s) of whichever host it was dropped
 * into:
 *
 * - Single-media host (.media-block__wrapper): the overlay stays where it
 *   rendered and binds to the wrapper's one video. Same contract as the
 *   original inline scripts.
 * - Social Media Videos ([data-smv]): the rendered overlay is a hidden source
 *   in the video list; a clone goes into the popup's [data-vc-slot] and binds
 *   to the one popup video every clip plays in. The block fires `smv:popup`
 *   once its popup has settled (it moves to <body>), which re-runs the scan.
 * - Media with Text: the block renders in the section's content column and its
 *   inline script moves it (one copy per video, desktop and phone) into the
 *   section's .sugar-mwt__desktop-media / __mobile-media while the page parses;
 *   each copy binds to the video beside it.
 * - Multi-video host ([data-pmg-host], the product gallery family): the
 *   rendered overlay is a hidden source; a clone is placed inside every
 *   [data-vc-slot] (one per video slide, Splide loop clones included) and
 *   each clone binds to its slot's video. A `gallery:init` event on the
 *   gallery re-runs the scan, which also covers slides rebuilt by variant
 *   filtering.
 *
 * Bindings are idempotent per video (a fresh clone of a slide carries no JS
 * properties, so rebuilt slides rebind cleanly).
 */
window.SugarVideoControls = (function () {
  'use strict';

  // m:ss, or h:mm:ss from an hour up
  function clock(t) {
    t = Math.max(0, Math.floor(t || 0));
    var h = Math.floor(t / 3600);
    var m = Math.floor((t % 3600) / 60);
    var s = t % 60;
    return (h ? h + ':' + (m < 10 ? '0' : '') : '') + m + ':' + (s < 10 ? '0' : '') + s;
  }

  // Control bar: play/pause, elapsed/total time, a progress line that seeks, sound (with a volume
  // slider for a mouse) and an optional speed menu. The bar is server-rendered out of sight and
  // shows from the first play (data-vc-bar-on); from then on it is the video's controller.
  function bindBar(c, bar, v, start) {
    var show = function () {
      if (!c.hasAttribute('data-vc-bar-on')) c.setAttribute('data-vc-bar-on', '');
    };
    v.addEventListener('play', show);
    if (!v.paused) show();

    var stop = function (e) {
      e.stopPropagation();
    };
    // A press on the bar never reaches the slider the video sits in, which would drag the slide
    bar.addEventListener('pointerdown', stop);
    bar.addEventListener('touchstart', stop, { passive: true });

    var pp = bar.querySelector('.vc-bar__play');
    var ppPlay = pp.querySelector('.vc-bar__play-face');
    var ppPause = pp.querySelector('.vc-bar__pause-face');
    var syncPlay = function () {
      var playing = !v.paused;
      ppPlay.style.display = playing ? 'none' : '';
      ppPause.style.display = playing ? '' : 'none';
      pp.setAttribute('aria-label', playing ? 'Pause video' : 'Play video');
    };
    pp.addEventListener('click', function (e) {
      e.stopPropagation();
      if (v.paused) start();
      else v.pause();
    });

    // Time and progress. The progress line follows every frame while the video plays and the
    // text is only rewritten when the second changes.
    var cur = bar.querySelector('[data-vc-cur]');
    var dur = bar.querySelector('[data-vc-dur]');
    var seek = bar.querySelector('.vc-bar__seek');
    var lastCur = '';
    var lastDur = '';
    var lastP = -1;
    var paint = function () {
      var d = v.duration;
      var ok = d > 0 && isFinite(d);
      var t = v.currentTime || 0;
      var p = ok ? Math.min(t / d, 1) : 0;
      if (p !== lastP) {
        lastP = p;
        seek.style.setProperty('--vc-p', p);
      }
      var cs = clock(t);
      var ds = ok ? clock(d) : '0:00';
      if (cs === lastCur && ds === lastDur) return;
      if (cs !== lastCur) cur.textContent = cs;
      if (ds !== lastDur) {
        dur.textContent = ds;
        seek.setAttribute('aria-valuemax', ok ? Math.round(d) : 0);
      }
      lastCur = cs;
      lastDur = ds;
      seek.setAttribute('aria-valuenow', Math.floor(t));
      seek.setAttribute('aria-valuetext', cs + ' of ' + ds);
    };
    var raf = 0;
    var tick = function () {
      paint();
      raf = !v.paused && !v.ended ? requestAnimationFrame(tick) : 0;
    };
    v.addEventListener('play', function () {
      syncPlay();
      if (!raf) raf = requestAnimationFrame(tick);
    });
    v.addEventListener('pause', syncPlay);
    v.addEventListener('ended', syncPlay);
    ['timeupdate', 'durationchange', 'loadedmetadata', 'seeked', 'emptied'].forEach(function (ev) {
      v.addEventListener(ev, paint);
    });
    syncPlay();
    paint();
    if (!v.paused && !raf) raf = requestAnimationFrame(tick);

    // Press anywhere on the line to jump there, drag to scrub; the pointer is captured so a scrub
    // can wander off the line. Arrow keys step 5s, Page Up/Down a tenth, Home/End the ends.
    var seekTo = function (e) {
      var r = seek.getBoundingClientRect();
      var d = v.duration;
      if (!r.width || !(d > 0) || !isFinite(d)) return;
      var x = Math.min(Math.max(e.clientX - r.left, 0), r.width);
      v.currentTime = (x / r.width) * d;
      paint();
    };
    var scrubbing = false;
    seek.addEventListener('pointerdown', function (e) {
      if (e.button) return;
      e.preventDefault();
      scrubbing = true;
      if (seek.setPointerCapture) seek.setPointerCapture(e.pointerId);
      seekTo(e);
    });
    seek.addEventListener('pointermove', function (e) {
      if (scrubbing) seekTo(e);
    });
    var done = function () {
      scrubbing = false;
    };
    seek.addEventListener('pointerup', done);
    seek.addEventListener('pointercancel', done);
    seek.addEventListener('touchmove', function (e) {
      e.stopPropagation();
      e.preventDefault();
    }, { passive: false });
    seek.addEventListener('click', stop);
    seek.addEventListener('keydown', function (e) {
      var d = v.duration;
      if (!(d > 0) || !isFinite(d)) return;
      var t = v.currentTime;
      var k = e.key;
      if (k === 'ArrowRight' || k === 'ArrowUp') t += 5;
      else if (k === 'ArrowLeft' || k === 'ArrowDown') t -= 5;
      else if (k === 'PageUp') t += d / 10;
      else if (k === 'PageDown') t -= d / 10;
      else if (k === 'Home') t = 0;
      else if (k === 'End') t = d;
      else return;
      e.preventDefault();
      v.currentTime = Math.min(Math.max(t, 0), d);
      paint();
    });

    // Sound: the button mutes and unmutes, the slider sets the volume. Both follow the video's own
    // state, so a host or the browser muting it keeps them true.
    var mute = bar.querySelector('.vc-bar__mute');
    var mOn = mute.querySelector('.vc-bar__sound-on');
    var mOff = mute.querySelector('.vc-bar__sound-off');
    var vol = bar.querySelector('.vc-bar__volume');
    var lastOff = null;
    var syncSound = function () {
      var off = v.muted || v.volume === 0;
      if (off !== lastOff) {
        lastOff = off;
        mOn.style.display = off ? 'none' : '';
        mOff.style.display = off ? '' : 'none';
        mute.setAttribute('aria-label', off ? 'Unmute' : 'Mute');
      }
      if (vol) {
        var val = off ? 0 : v.volume;
        if (parseFloat(vol.value) !== val) vol.value = val;
        vol.style.setProperty('--vc-vol', val);
      }
    };
    mute.addEventListener('click', function (e) {
      e.stopPropagation();
      if (v.muted || v.volume === 0) {
        if (v.volume === 0) v.volume = 1;
        v.muted = false;
      } else {
        v.muted = true;
      }
    });
    if (vol) {
      vol.addEventListener('input', function () {
        var x = parseFloat(vol.value) || 0;
        v.volume = x;
        v.muted = x === 0;
      });
      vol.addEventListener('click', stop);
    }
    v.addEventListener('volumechange', syncSound);
    syncSound();

    // Speed menu
    var sp = bar.querySelector('.vc-bar__speed-btn');
    var menu = bar.querySelector('.vc-bar__menu');
    if (sp && menu) {
      var label = sp.querySelector('[data-vc-rate]');
      var items = Array.prototype.slice.call(menu.querySelectorAll('[data-rate]'));
      var outside = function (e) {
        var path = e.composedPath ? e.composedPath() : [];
        if (path.indexOf(menu) === -1 && path.indexOf(sp) === -1) close(false);
      };
      var close = function (refocus) {
        if (menu.hidden) return;
        menu.hidden = true;
        sp.setAttribute('aria-expanded', 'false');
        document.removeEventListener('click', outside, true);
        if (refocus) sp.focus();
      };
      var open = function () {
        menu.hidden = false;
        sp.setAttribute('aria-expanded', 'true');
        document.addEventListener('click', outside, true);
        var on = menu.querySelector('[aria-checked="true"]') || items[0];
        if (on) on.focus({ preventScroll: true });
      };
      var syncRate = function () {
        var r = v.playbackRate;
        label.textContent = r + 'x';
        sp.setAttribute('aria-label', 'Playback speed ' + r + 'x');
        items.forEach(function (it) {
          it.setAttribute('aria-checked', parseFloat(it.getAttribute('data-rate')) === r ? 'true' : 'false');
        });
      };
      sp.addEventListener('click', function (e) {
        e.stopPropagation();
        if (menu.hidden) open();
        else close(true);
      });
      sp.addEventListener('keydown', function (e) {
        if (e.key === 'Escape') close(true);
      });
      items.forEach(function (it) {
        it.addEventListener('click', function (e) {
          e.stopPropagation();
          var r = parseFloat(it.getAttribute('data-rate')) || 1;
          v.playbackRate = r;
          // A video that reloads (the lazy loader, a source swap) keeps the picked speed
          v.defaultPlaybackRate = r;
          close(true);
        });
      });
      menu.addEventListener('keydown', function (e) {
        var i = items.indexOf(document.activeElement);
        var k = e.key;
        if (k === 'Escape') {
          e.preventDefault();
          close(true);
        } else if (k === 'Tab') {
          close(false);
        } else if (k === 'ArrowDown' || k === 'ArrowUp' || k === 'Home' || k === 'End') {
          e.preventDefault();
          var n = items.length;
          var j = k === 'Home' ? 0 : k === 'End' ? n - 1 : (i + (k === 'ArrowDown' ? 1 : -1) + n) % n;
          items[j].focus();
        }
      });
      v.addEventListener('ratechange', syncRate);
      syncRate();
    }
  }

  function bindVideo(c, v, w) {
    var pb = c.querySelector('.media-block__play-button');
    var bar = c.querySelector('.vc-bar');
    // A pill with label or a control bar starts a video that waited for the click with its sound
    // on (a host may ship it muted); a muted autoplay video stays muted when a click restarts it.
    var soundOnClick = !v.autoplay && (c.getAttribute('data-play-style') === 'pill' || !!bar);
    var start = function () {
      if (soundOnClick) v.muted = false;
      v.play();
    };
    if (pb || bar || v.autoplay) v.controls = false;
    if (pb || bar) {
      v.addEventListener('click', function () {
        if (v.paused) start();
        else v.pause();
      });
    }
    if (bar) bindBar(c, bar, v, start);

    // Hide overlays while playing: the Media root carries sugar-video-controls--playing while its
    // video plays (block-video-controls.css fades its overlays). Written only when it changes.
    var media = c.hasAttribute('data-hide-overlays') && w.closest('.sugar-media');
    if (media) {
      media.classList.add('sugar-video-controls--fade');
      var mark = function () {
        var on = !v.paused && !v.ended;
        if (media.classList.contains('sugar-video-controls--playing') !== on) media.classList.toggle('sugar-video-controls--playing', on);
      };
      v.addEventListener('play', mark);
      v.addEventListener('pause', mark);
      v.addEventListener('ended', mark);
      mark();
    }

    if (pb) {
      // A button that carries a pause glyph (Show pause button while playing) stays on screen
      // and swaps glyphs with the video's state, and a press while playing pauses; otherwise
      // the button hides while the video plays.
      var playFace = pb.querySelectorAll('.vc-play-face');
      var pauseFace = pb.querySelectorAll('.vc-pause-face');
      var swap = pauseFace.length > 0;
      var upd = function () {
        if (swap) {
          var playing = !v.paused;
          playFace.forEach(function (el) {
            el.style.display = playing ? 'none' : '';
          });
          pauseFace.forEach(function (el) {
            el.style.display = playing ? '' : 'none';
          });
          pb.setAttribute('aria-label', playing ? 'Pause video' : 'Play video');
          return;
        }
        pb.style.opacity = v.paused ? '' : '0';
        pb.style.pointerEvents = v.paused ? '' : 'none';
      };
      pb.addEventListener('click', function () {
        if (swap && !v.paused) v.pause();
        else start();
      });
      v.addEventListener('play', upd);
      v.addEventListener('pause', upd);
      if (v.autoplay) v.addEventListener('playing', upd, { once: true });
      // A video that started before the engine bound shows its pause glyph straight away
      if (swap) upd();

      // A pause button in the middle of the picture steps aside while the video plays: it shows
      // while the mouse moves over the video, for a moment after a tap or a click, while it has
      // keyboard focus, and whenever the video is paused. A corner button stays put.
      if (swap && (c.getAttribute('data-play-pos') || 'center') === 'center') {
        var idleTimer = null;
        var rest = function () {
          if (!v.paused && !pb.matches(':focus-visible')) c.setAttribute('data-vc-idle', '');
        };
        var wake = function (ms) {
          clearTimeout(idleTimer);
          c.removeAttribute('data-vc-idle');
          if (!v.paused) idleTimer = setTimeout(rest, ms);
        };
        v.addEventListener('play', function () {
          wake(1500);
        });
        v.addEventListener('pause', function () {
          wake(0);
        });
        w.addEventListener('pointermove', function (e) {
          if (e.pointerType === 'mouse') wake(1500);
        });
        w.addEventListener('pointerdown', function (e) {
          if (e.pointerType !== 'mouse') wake(2000);
        });
        w.addEventListener('pointerleave', function (e) {
          if (e.pointerType !== 'mouse' || v.paused) return;
          clearTimeout(idleTimer);
          c.setAttribute('data-vc-idle', '');
        });
        pb.addEventListener('focus', function () {
          wake(1500);
        });
        if (!v.paused) wake(1500);
      }
    }

    var tl = c.querySelector('.media-block__timeline-progress');
    if (tl) {
      var paint = function () {
        if (!v.duration) return;
        var r = v.currentTime / v.duration;
        tl.style.transform = 'scaleX(' + r + ')';
        // The control bar's pill clips its fill with this instead, so the rounded ends never squash
        tl.style.setProperty('--vc-p', r);
      };
      v.addEventListener('timeupdate', paint);

      // The bar seeks: press anywhere on it to jump there, drag to scrub. The pointer is
      // captured so a scrub can wander off the bar, and the touch never reaches the slider
      // the video sits in, which would otherwise drag the slide instead.
      var bar = tl.parentElement;
      var seekTo = function (e) {
        var r = bar.getBoundingClientRect();
        if (!r.width || !v.duration) return;
        var x = Math.min(Math.max(e.clientX - r.left, 0), r.width);
        v.currentTime = (x / r.width) * v.duration;
        paint();
      };
      var scrubbing = false;
      bar.addEventListener('pointerdown', function (e) {
        if (e.button) return;
        e.preventDefault();
        e.stopPropagation();
        scrubbing = true;
        if (bar.setPointerCapture) bar.setPointerCapture(e.pointerId);
        seekTo(e);
      });
      bar.addEventListener('pointermove', function (e) {
        if (scrubbing) seekTo(e);
      });
      var done = function () {
        scrubbing = false;
      };
      bar.addEventListener('pointerup', done);
      bar.addEventListener('pointercancel', done);
      bar.addEventListener('touchstart', function (e) { e.stopPropagation(); }, { passive: true });
      bar.addEventListener('touchmove', function (e) { e.stopPropagation(); e.preventDefault(); }, { passive: false });
      bar.addEventListener('click', function (e) { e.stopPropagation(); });
    }

    var rb = c.querySelector('.media-block__replay-button');
    if (rb) {
      rb.addEventListener('click', function (e) {
        e.stopPropagation();
        v.currentTime = 0;
        v.play();
      });
    }

    var fb = c.querySelector('.media-block__fullscreen-button');
    if (fb) {
      var fse = fb.querySelector('.fs-icon--enter');
      var fsx = fb.querySelector('.fs-icon--exit');
      var useVideoFs = !!(v.webkitEnterFullscreen && !w.requestFullscreen && !w.webkitRequestFullscreen);
      var fsIsActive = function () {
        if (useVideoFs) return v.webkitDisplayingFullscreen;
        return (document.fullscreenElement || document.webkitFullscreenElement) === w;
      };
      var fsExit = function () {
        if (useVideoFs) {
          v.webkitExitFullscreen();
          return;
        }
        (document.exitFullscreen || document.webkitExitFullscreen).call(document);
      };
      var fsSize = function () {
        if (!fsIsActive() || !v.videoWidth) return;
        var sw = w.clientWidth;
        var sh = w.clientHeight;
        var vr = v.videoWidth / v.videoHeight;
        if (vr > sw / sh) {
          v.style.width = sw + 'px';
          v.style.height = sw / vr + 'px';
        } else {
          v.style.height = sh + 'px';
          v.style.width = sh * vr + 'px';
        }
      };
      var fsu = function () {
        var isFs = fsIsActive();
        fse.style.display = isFs ? 'none' : '';
        fsx.style.display = isFs ? '' : 'none';
        if (isFs) fsSize();
        else {
          v.style.width = '';
          v.style.height = '';
        }
      };
      fb.addEventListener('click', function (e) {
        e.stopPropagation();
        if (fsIsActive()) fsExit();
        else if (useVideoFs) v.webkitEnterFullscreen();
        else (w.requestFullscreen || w.webkitRequestFullscreen).call(w);
      });
      w.addEventListener('click', function (e) {
        if (e.target === w && fsIsActive()) fsExit();
      });
      window.addEventListener('resize', fsSize);
      document.addEventListener('fullscreenchange', fsu);
      document.addEventListener('webkitfullscreenchange', fsu);
      if (useVideoFs) {
        v.addEventListener('webkitbeginfullscreen', fsu);
        v.addEventListener('webkitendfullscreen', fsu);
      }
    }

    var sb = c.querySelector('.media-block__sound-button');
    if (sb) {
      var me = sb.querySelector('.sound-icon--muted') || sb.querySelector('.sound-text--muted');
      var ue = sb.querySelector('.sound-icon--unmuted') || sb.querySelector('.sound-text--unmuted');
      var su = function () {
        me.style.display = v.muted ? '' : 'none';
        ue.style.display = v.muted ? 'none' : '';
      };
      sb.addEventListener('click', function (e) {
        e.stopPropagation();
        v.muted = !v.muted;
        su();
      });
      // A host that mutes or unmutes the video itself (a popup unmuting after play) keeps the icon true
      v.addEventListener('volumechange', su);
      su();
    }
  }

  function scanHost(host, overlay) {
    host.querySelectorAll('[data-vc-slot]').forEach(function (slot) {
      var v = slot.querySelector('video');
      if (!v) return;
      var clone = slot.querySelector('.video-controls');
      if (!clone) {
        clone = overlay.cloneNode(true);
        slot.appendChild(clone);
      }
      if (v.__vcBound) return;
      v.__vcBound = true;
      bindVideo(clone, v, slot);
    });
  }

  function attach(overlay) {
    if (!overlay) return;

    var single = overlay.closest('.media-block__wrapper');
    if (single) {
      var v = single.querySelector('.media-block__video');
      if (!v || v.__vcBound) return;
      v.__vcBound = true;
      bindVideo(overlay, v, single);
      return;
    }

    var mwt = overlay.closest('.sugar-mwt__desktop-media, .sugar-mwt__mobile-media');
    if (mwt) {
      var mv = mwt.querySelector('video');
      if (!mv || mv.__vcBound) return;
      mv.__vcBound = true;
      bindVideo(overlay, mv, mwt);
      return;
    }

    var smv = overlay.closest('[data-smv]');
    if (smv) {
      var id = smv.getAttribute('data-smv');
      var scanPopup = function () {
        // An editor re-render replaces the overlay; the stale one stops answering
        if (!overlay.isConnected) return;
        document.querySelectorAll('[data-popup-modal="' + id + '"]').forEach(function (popup) {
          scanHost(popup, overlay);
        });
      };
      scanPopup();
      document.addEventListener('smv:popup', function (e) {
        if (e.detail && e.detail.id === id) scanPopup();
      });
      return;
    }

    var host = overlay.closest('[data-pmg-host]');
    if (!host) return;

    var scan = function () {
      scanHost(host, overlay);
    };
    scan();

    var main = host.querySelector('.product-media-gallery__main');
    if (main && !main.__vcRescan) {
      main.__vcRescan = true;
      main.addEventListener('gallery:init', scan);
    }
  }

  return { attach: attach, bindVideo: bindVideo };
})();
